import React from 'react';
import { Formik, Field } from 'formik';
import {
  Link,
  Button,
  TextField,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { getThemeColors, isLightColor } from './themeConfig';

interface AuthFormProps {
  theme: 'light' | 'dark';
  initialValues: any;
  validationSchema: any;
  onSubmit: (values: any) => void;
  children: React.ReactNode;
  submitButtonText: string;
  isSubmitting?: boolean;
  disabled?: boolean;
}

interface AuthFieldProps {
  theme: 'light' | 'dark';
  label: string;
  name: string;
  type?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  maxLength?: number;
}

interface AuthButtonProps {
  theme: 'light' | 'dark';
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  fullWidth?: boolean;
}

interface AuthLinkProps {
  theme: 'light' | 'dark';
  onClick: () => void;
  children: React.ReactNode;
  ariaLabel?: string;
  /** Overrides the link colour. Defaults to the theme's text colour. */
  color?: string;
}

interface AuthMessageProps {
  theme: 'light' | 'dark';
  type: 'error' | 'success' | 'info';
  message: string;
}

export const AuthForm: React.FC<AuthFormProps> = ({
  theme,
  initialValues,
  validationSchema,
  onSubmit,
  children,
  submitButtonText,
  isSubmitting = false,
  disabled = false
}) => {
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({ handleSubmit, errors, touched, values }) => (
        <form onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="column" style={{ gap: '16px' }}>
            {children}

            <Box mt={2}>
              <AuthButton
                theme={theme}
                variant="primary"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  const form = e.currentTarget.closest('form');
                  if (form) {
                    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                  }
                }}
                disabled={disabled || isSubmitting}
                loading={isSubmitting}
                fullWidth
              >
                {isSubmitting ? 'Loading...' : submitButtonText}
              </AuthButton>

              {/* Hidden submit button for Enter key support */}
              <button type="submit" style={{ display: 'none' }} aria-hidden="true">
                {submitButtonText}
              </button>
            </Box>
          </Box>
        </form>
      )}
    </Formik>
  );
};

export const AuthField: React.FC<AuthFieldProps> = ({
  theme,
  label,
  name,
  type = 'text',
  error = false,
  helperText = '',
  disabled = false,
  required = false,
  maxLength
}) => {
  // Get theme colors to determine text color
  const colors = getThemeColors(theme);

  return (
    <Field name={name}>
      {({ field, meta }: any) => (
        <TextField
          {...field}
          type={type}
          label={label}
          variant="outlined"
          fullWidth
          disabled={disabled}
          required={required}
          error={error || (meta.touched && !!meta.error)}
          helperText={helperText || (meta.touched && meta.error ? meta.error : '')}
          placeholder={label}
          inputProps={{
            ...(maxLength ? { maxLength } : {}),
          }}
          InputProps={{
            style: {
              color: colors.text,
            },
            sx: {
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.text,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.text,
              },
            },
          }}
          InputLabelProps={{
            style: {
              color: colors.textSecondary,
            },
            sx: {
              '&.Mui-focused': {
                color: colors.text,
              },
            },
          }}
          FormHelperTextProps={{
            style: {
              color: colors.textSecondary,
            },
            sx: {
              '&.Mui-error': {
                color: '#f44336', // Error color for both themes
              },
            },
          }}
        />
      )}
    </Field>
  );
};

export const AuthButton: React.FC<AuthButtonProps> = ({
  theme,
  variant = 'primary',
  onClick,
  disabled = false,
  loading = false,
  children,
  fullWidth = false
}) => {
  // Get theme colors to determine text color
  const colors = getThemeColors(theme);

  const getButtonVariant = () => {
    switch (variant) {
      case 'primary':
        return 'contained';
      case 'secondary':
        return 'outlined';
      case 'danger':
        return 'contained';
      case 'success':
        return 'contained';
      default:
        return 'contained';
    }
  };

  const getButtonColor = () => {
    switch (variant) {
      case 'primary':
        return 'primary';
      case 'secondary':
        return 'primary';
      case 'danger':
        return 'default';
      case 'success':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getButtonStyle = () => {
    // Determine text color based on primary color brightness (not theme)
    const isPrimaryLight = isLightColor(colors.primary);
    const buttonTextColor = isPrimaryLight ? '#000000' : '#FFFFFF';

    switch (variant) {
      case 'success':
        return {
          backgroundColor: '#28A745',
          color: 'white'
        };
      case 'danger':
        return {
          backgroundColor: '#DC3545',
          color: 'white'
        };
      case 'primary':
        return {
          backgroundColor: colors.primary,
          color: buttonTextColor
        };
      default:
        return {
          backgroundColor: colors.primary,
          color: buttonTextColor
        };
    }
  };

  return (
    <Button
      variant={getButtonVariant()}
      color={getButtonColor()}
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      startIcon={loading ? <CircularProgress size={16} /> : undefined}
      style={getButtonStyle()}
    >
      {children}
    </Button>
  );
};

export const AuthLink: React.FC<AuthLinkProps> = ({
  theme,
  onClick,
  children,
  ariaLabel,
  color
}) => {
  // Get theme colors to determine text color
  const colors = getThemeColors(theme);

  return (
    <Link
      component="button"
      variant="body2"
      onClick={onClick}
      aria-label={ariaLabel}
      type="button"
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: color || colors.text,
        textDecoration: 'underline'
      }}
      role="button"
    >
      {children}
    </Link>
  );
};

export const AuthMessage: React.FC<AuthMessageProps> = ({
  theme,
  type,
  message
}) => {

  const getMessageStyle = () => {
    const baseStyle = {
      padding: '8px 16px',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: 400,
      textAlign: 'center' as const,
      marginBottom: '16px',
    };

    switch (type) {
      case 'error':
        return {
          ...baseStyle,
          color: theme === 'dark' ? '#ff6b6b' : '#d32f2f',
          backgroundColor: theme === 'dark' ? 'rgba(255, 107, 107, 0.1)' : 'rgba(211, 47, 47, 0.1)',
          border: `1px solid ${theme === 'dark' ? 'rgba(255, 107, 107, 0.3)' : 'rgba(211, 47, 47, 0.3)'}`,
        };
      case 'success':
        return {
          ...baseStyle,
          color: theme === 'dark' ? '#4caf50' : '#2e7d32',
          backgroundColor: theme === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)',
          border: `1px solid ${theme === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(46, 125, 50, 0.3)'}`,
        };
      case 'info':
        return {
          ...baseStyle,
          color: theme === 'dark' ? '#2196f3' : '#1976d2',
          backgroundColor: theme === 'dark' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(25, 118, 210, 0.1)',
          border: `1px solid ${theme === 'dark' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
        };
      default:
        return baseStyle;
    }
  };

  return (
    <div style={getMessageStyle()}>
      {message}
    </div>
  );
};
